import React, { useState, useEffect } from 'react';
import { Table, Space, Button, Input, Tooltip, Popconfirm } from 'antd';
import { FilterOutlined, SendOutlined, CheckCircleOutlined, FolderOutlined, DownloadOutlined } from '@ant-design/icons';
import { formatCurrency } from '../../../../utils/formatCurrency';
import { TIPOS_ORDEN_PAGO } from '../../../../constants/cons';

export default function TablaPagos({
  dataSource,
  loading,
  onVerDetalle,
  currentPage,
  onPageChange,
  pageSize,
  estatusSeleccionado,
  onSolicitarFactura,
  onFacturaPagada,
  onArchivar,
  onDescargarDocumento,
  loadingDescarga = {},
  filtrosColumnas = {},
  onActualizarFiltrosColumnas,
  sortOrder = {},
  onActualizarSortOrder
}) {
  const [loadingArchivar, setLoadingArchivar] = useState({});
  const [filteredInfo, setFilteredInfo] = useState(filtrosColumnas);
  const [filterSearchValues, setFilterSearchValues] = useState({});
  const [sortedInfo, setSortedInfo] = useState(sortOrder);
  const [currentDataSource, setCurrentDataSource] = useState(dataSource || []);

  useEffect(() => {
    setFilteredInfo(filtrosColumnas);
  }, [filtrosColumnas]);

  useEffect(() => {
    setSortedInfo(sortOrder);
  }, [sortOrder]);

  useEffect(() => {
    setCurrentDataSource(dataSource || []);
  }, [dataSource]);

  const proveedorFilters = [...new Set((dataSource || []).map(item => item.proveedor).filter(Boolean))]
    .map(proveedor => ({ text: proveedor, value: proveedor }));

  const clienteFilters = [...new Set((dataSource || []).map(item => item.clienteNombre).filter(Boolean))]
    .map(cliente => ({ text: cliente, value: cliente }));

  const razonSocialFilters = [...new Set((dataSource || []).map(item => item.razonSocial).filter(Boolean))]
    .map(razonSocial => ({ text: razonSocial, value: razonSocial }));

  const getSearchableFilterProps = (dataIndex, filters) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => {
      const searchValue = filterSearchValues[dataIndex] || '';

      const filteredOptions = filters.filter(filter =>
        filter.text.toLowerCase().includes(searchValue.toLowerCase())
      );

      return (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Buscar..."
            value={searchValue}
            onChange={(e) => {
              setFilterSearchValues(prev => ({
                ...prev,
                [dataIndex]: e.target.value
              }));
            }}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <div style={{ marginBottom: 8, maxHeight: 300, overflowY: 'auto' }}>
            {filteredOptions.map((filter) => (
              <div key={filter.value} style={{ marginBottom: 4 }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={selectedKeys.includes(filter.value)}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      let newKeys;
                      if (checked) {
                        newKeys = [...selectedKeys, filter.value];
                      } else {
                        newKeys = selectedKeys.filter(key => key !== filter.value);
                      }
                      setSelectedKeys(newKeys);
                      setTimeout(() => confirm({ closeDropdown: false }), 0);
                    }}
                    style={{ marginRight: 8 }}
                  />
                  <span>{filter.text}</span>
                </label>
              </div>
            ))}
          </div>
          <div className="ant-table-filter-dropdown-btns">
            <Button
              onClick={() => {
                clearFilters();
                setFilterSearchValues(prev => ({
                  ...prev,
                  [dataIndex]: ''
                }));
                confirm({ closeDropdown: false });
              }}
              size="small"
              style={{ width: 90 }}
            >
              Limpiar
            </Button>
            <Button
              type="link"
              size="small"
              onClick={() => {
                close();
              }}
            >
              Cerrar
            </Button>
          </div>
        </div>
      );
    },
    filterIcon: (filtered) => (
      <FilterOutlined className={filtered ? 'ant-table-filter-icon-active' : ''} />
    ),
    onFilter: (value, record) => record[dataIndex] === value,
  });

  const columns = [
    {
      title: 'OP',
      dataIndex: 'ordenPagoID',
      key: 'ordenPagoID',
      width: 80,
      align: 'center',
      sorter: (a, b) => a.ordenPagoID - b.ordenPagoID,
      sortOrder: sortedInfo.columnKey === 'ordenPagoID' ? sortedInfo.order : null,
      render: (value, record) =>
        <><strong>{value}</strong> ({record.numeroServicios ?? 0})</>

    },
    {
      title: 'Proveedor',
      dataIndex: 'proveedor',
      key: 'proveedor',
      width: 280,
      align: 'center',
      sorter: (a, b) => (a.proveedor || '').localeCompare(b.proveedor || ''),
      sortOrder: sortedInfo.columnKey === 'proveedor' ? sortedInfo.order : null,
      filteredValue: filteredInfo.proveedor || null,
      ...getSearchableFilterProps('proveedor', proveedorFilters)
    },
    {
      title: 'Razón Social',
      dataIndex: 'razonSocial',
      key: 'razonSocial',
      width: 200,
      align: 'center',
      ellipsis: true,
      sorter: (a, b) => (a.razonSocial || '').localeCompare(b.razonSocial || ''),
      sortOrder: sortedInfo.columnKey === 'razonSocial' ? sortedInfo.order : null,
      filteredValue: filteredInfo.razonSocial || null,
      ...getSearchableFilterProps('razonSocial', razonSocialFilters)
    },
    {
      title: 'Cliente',
      dataIndex: 'clienteNombre',
      key: 'clienteNombre',
      width: 120,
      align: 'center',
      sorter: (a, b) => (a.clienteNombre || '').localeCompare(b.clienteNombre || ''),
      sortOrder: sortedInfo.columnKey === 'clienteNombre' ? sortedInfo.order : null,
      filteredValue: filteredInfo.clienteNombre || null,
      ...getSearchableFilterProps('clienteNombre', clienteFilters)
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 80,
      align: 'center',
      render: (val) => <strong>{formatCurrency(val)}</strong>,
      sorter: (a, b) => (a.total || 0) - (b.total || 0),
      sortOrder: sortedInfo.columnKey === 'total' ? sortedInfo.order : null
    },
    // {
    //   title: 'Tipo OP',
    //   dataIndex: 'tipoOp',
    //   key: 'tipoOp',
    //   width: 100,
    //   align: 'center',
    //   render: (tipoOp) => TIPOS_ORDEN_PAGO[tipoOp] || '-',
    //   sorter: (a, b) => (a.tipoOp || 0) - (b.tipoOp || 0),
    //   sortOrder: sortedInfo.columnKey === 'tipoOp' ? sortedInfo.order : null
    // },
    {
      title: 'Fecha Creación',
      dataIndex: 'fechaCreacion',
      key: 'fechaCreacion',
      width: 110,
      align: 'center',
      render: (fecha) => fecha ? fecha.substring(0, 16) : '',
      sorter: (a, b) => (a.fechaCreacion || '').localeCompare(b.fechaCreacion || ''),
      sortOrder: sortedInfo.columnKey === 'fechaCreacion' ? sortedInfo.order : null
    },
    // {
    //   title: 'Fecha Autorización',
    //   dataIndex: 'fechaAutorizacionPago',
    //   key: 'fechaAutorizacionPago',
    //   width: 150,
    //   align: 'center',
    //   render: (fecha) => fecha && fecha !== '-' ? fecha : '-',
    //   sorter: (a, b) => (a.fechaAutorizacionPago || '').localeCompare(b.fechaAutorizacionPago || ''),
    //   sortOrder: sortedInfo.columnKey === 'fechaAutorizacionPago' ? sortedInfo.order : null
    // },
    ...(estatusSeleccionado === 6 ? [{
      title: 'Fecha Carga Factura',
      dataIndex: 'fechaCargaFactura',
      key: 'fechaCargaFactura',
      width: 130,
      align: 'center',
      render: (fecha) => fecha || '-',
      sorter: (a, b) => (a.fechaCargaFactura || '').localeCompare(b.fechaCargaFactura || ''),
      sortOrder: sortedInfo.columnKey === 'fechaCargaFactura' ? sortedInfo.order : null
    }] : []),
    {
      title: 'Acciones',
      key: 'acciones',
      width: 80,
      align: 'center',
      render: (_, record) => {
        const tieneFacturaPagada = !!record.fechaPagado;

        const handleClick = (e, callback) => {
          e.stopPropagation();
          callback(record);
        };

        const handleArchivarClick = () => {
          setLoadingArchivar(prev => ({ ...prev, [record.ordenPagoID]: true }));
          onArchivar(record).finally(() => {
            setLoadingArchivar(prev => ({ ...prev, [record.ordenPagoID]: false }));
          });
        };

        const botonArchivar = (
          <div onClick={(e) => e.stopPropagation()}>
            <Popconfirm
              title={record.archivado ? "¿Desarchivar orden de pago?" : "¿Archivar orden de pago?"}
              description={record.archivado ? "Esta orden volverá a estar visible en el listado principal." : "¿Estás seguro de que deseas archivar esta orden de pago?"}
              onConfirm={handleArchivarClick}
              okText="Sí, continuar"
              cancelText="Cancelar"
              okButtonProps={{ danger: !record.archivado }}
              overlayClassName="popconfirm-rounded"
            >
              <Tooltip title={record.archivado ? "Desarchivar" : "Archivar"}>
                <Button
                  type={record.archivado ? "primary" : "text"}
                  size="small"
                  icon={<FolderOutlined />}
                  loading={loadingArchivar[record.ordenPagoID]}
                />
              </Tooltip>
            </Popconfirm>
          </div>
        );

        if (estatusSeleccionado === 4) {
          return (
            <Space size="small">
              <Tooltip title="Solicitar Factura">
                <Button
                  type="text"
                  size="small"
                  icon={<SendOutlined />}
                  onClick={(e) => handleClick(e, onSolicitarFactura)}
                />
              </Tooltip>
              {botonArchivar}
            </Space>
          );
        }

        if (estatusSeleccionado === 7) {
          return (
            <Space size="small">
              <Tooltip title="Marcar Pagado">
                <Button
                  type={tieneFacturaPagada ? "default" : "text"}
                  size="small"
                  icon={<CheckCircleOutlined />}
                  onClick={(e) => handleClick(e, onFacturaPagada)}
                  disabled={tieneFacturaPagada}
                  style={tieneFacturaPagada ? { backgroundColor: '#52c41a', borderColor: '#52c41a', color: '#fff' } : {}}
                />
              </Tooltip>
              {botonArchivar}
            </Space>
          );
        }

        if (estatusSeleccionado === 9) {
          return (
            <Space size="small">
              <Tooltip title="Descargar Comprobante">
                <Button
                  type="text"
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={(e) => handleClick(e, onDescargarDocumento)}
                  loading={loadingDescarga[record.ordenPagoID]}
                  disabled={loadingDescarga[record.ordenPagoID]}
                />
              </Tooltip>
              {botonArchivar}
            </Space>
          )
        }

        return <Space size="small">{botonArchivar}</Space>;
      }
    }
  ];

  const handleTableChange = (pagination, filters, sorter, { currentDataSource: cds }) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
    setCurrentDataSource(cds);

    if (onActualizarFiltrosColumnas) {
      onActualizarFiltrosColumnas(filters);
    }

    if (onActualizarSortOrder) {
      onActualizarSortOrder(sorter);
    }

    if (pagination.current !== currentPage || pagination.pageSize !== pageSize) {
      onPageChange(pagination.current, pagination.pageSize);
    }
  };

  const sumaTotal = currentDataSource.reduce((acc, record) => acc + (record.total || 0), 0);

  return (
    <Table
      columns={columns}
      dataSource={loading ? [] : dataSource}
      rowKey="ordenPagoID"
      loading={loading}
      pagination={{
        current: currentPage || 1,
        pageSize,
        showSizeChanger: true,
        pageSizeOptions: ['10', '25', '50', '100', '200'],
        showTotal: (total) => (
          <span>
            Registros: <strong>{total.toLocaleString('en-US')}</strong> Total: <strong>${sumaTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
          </span>
        )
      }}
      size="small"
      locale={{
        emptyText: loading ? ' ' : 'No hay datos'
      }}
      onRow={(record) => ({
        onClick: () => onVerDetalle(record),
        style: { cursor: 'pointer' }
      })}
      onChange={handleTableChange}
    />
  );
}
